/**
 * Single source of truth for TanStack Query keys.
 *
 * Every useQuery and invalidateQueries call must build its key through this
 * factory. Keys used to be inline literals duplicated between the composable
 * that owns a query and the views that invalidate it after a mutation; when
 * one side changed shape the other kept matching nothing and the cache
 * silently served stale data (no error anywhere). With a single definition,
 * renaming or re-scoping a key is one edit and the compiler finds every use.
 */
export const queryKeys = {
  /**
   * The Wikipedia editions a league can be founded on. Unkeyed: the list is the
   * same for every player and changes at most once a day (#531).
   */
  wikipediaEditions: () => ["wikipedia-editions"] as const,
  /** Wikimedia-backed market article list for a league domain. */
  market: (domain: string | undefined) => ["market", domain] as const,
  /** Market search fallback, keyed by the (trimmed) search text. */
  marketSearch: (domain: string | undefined, search: string) =>
    ["market-search", domain, search] as const,
  /**
   * Market rows hydrated for owned articles the Top Read Snapshot doesn't
   * carry, keyed by the (sorted) titles being hydrated.
   */
  marketOwnedExtras: (domain: string | undefined, titles: readonly string[]) =>
    ["market-owned-extras", domain, ...titles] as const,
  /** All contracts held by any team in the league (ownership badges). */
  leagueContracts: (leagueId: string | null) =>
    ["league-contracts", leagueId] as const,
  /** The current player's saved lineup for the league. */
  teamLineup: (leagueId: string | null) => ["team-lineup", leagueId] as const,
  /** Chemistry levels for a draft formation's current placements. */
  chemistry: (leagueId: string | null, schema: string, placements: string[]) =>
    ["chemistry", leagueId, schema, ...placements] as const,
  /** The current player's team in the league (credits, name). */
  myTeam: (leagueId: string | null) => ["my-team", leagueId] as const,
  dashboard: (leagueId: string | null) => ["dashboard", leagueId] as const,
  leaderboard: (leagueId: string | null) => ["leaderboard", leagueId] as const,
  notifications: () => ["notifications"] as const,
  globalLeague: () => ["global-league"] as const,
  /** A single league by id — including ones the player has not joined yet. */
  league: (leagueId: string) => ["league", leagueId] as const,
  /** Every public league — the same answer for every player, so no key parts. */
  publicLeagues: () => ["public-leagues"] as const,
  /** A private league's invitation code, for a caller its policy lets invite. */
  invitationCode: (leagueId: string | null) =>
    ["invitation-code", leagueId] as const,
  /**
   * The league an invitation code resolves to. Keyed by the *normalized* code,
   * so the same invitation pasted three different ways is one cache entry and
   * one request — which matters when the request is rate limited.
   */
  leagueByCode: (code: string) => ["league-by-code", code] as const,
  /** Whether the caller plays this league, and whether they are its admin. */
  leagueRole: (leagueId: string | null) => ["league-role", leagueId] as const,
  /**
   * Another team's line-up, read-only. Deliberately not under `teamLineup`:
   * that key is the viewer's own and every lineup mutation invalidates it,
   * which would evict rivals the player is only looking at.
   */
  rivalLineup: (leagueId: string | null, teamId: string | null) =>
    ["rival-lineup", leagueId, teamId] as const,
};
